import { Box, CircularProgress } from '@mui/material';
import type { MunicipalitiesComparative } from '@/graphql/types/municipality';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { comparativeRowsToMapGeometry } from '@/utils/map/comparativeToMapGeometry';
import { MAPBOX_STYLE_LIGHT_V11 } from '@/utils/map/mapboxStyles';
import { useQuery } from '@apollo/client';
import { QUERY_DIMENSIONS } from '@/graphql/queries/dimensions';
import { useLanguage } from '@/hooks/useLanguage';
import { useTheme } from '@mui/material/styles';
import { DynamicIcon } from '@/components/atoms/dynamicIcon';
import { FloatingButtons } from '@/components/molecules/floatingButtons';
import { LegendButton } from '@/components/molecules/legendButton';
import { useTranslation } from '@/hooks/useTranslation';
import {
  ComparativeMapLayerButton,
  ComparativeMapLayersOverlay,
} from './ComparativeMapLayersOverlay';

const Map = lazy(() =>
  import('@/components/organisms/map').then((module) => ({
    default: module.Map,
  }))
);

const MAP_HEIGHT = { xs: 480, sm: 560, md: 620 };

function parentLayerValue(dimensionId: number): number {
  return dimensionId === 0 ? -10 : dimensionId;
}

export interface ComparativeMunicipalitiesMapProps {
  data: MunicipalitiesComparative[];
  dimensionId: number;
  selectedBigqueryId?: string;
  selectedDimensionName?: string;
  variant?: 'risk' | 'opportunity';
}

export const ComparativeMunicipalitiesMap = ({
  data,
  dimensionId,
  selectedBigqueryId,
  selectedDimensionName,
  variant: variantProp,
}: ComparativeMunicipalitiesMapProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const [selectedLayer, setSelectedLayer] = useState(() => parentLayerValue(dimensionId));
  const [layersOpen, setLayersOpen] = useState(false);
  const [mapVariant, setMapVariant] = useState<'risk' | 'opportunity'>(variantProp ?? 'risk');

  useEffect(() => {
    setSelectedLayer(parentLayerValue(dimensionId));
  }, [dimensionId]);

  useEffect(() => {
    if (variantProp) {
      setMapVariant(variantProp);
    }
  }, [variantProp]);

  const { data: dimensionsData, loading: dimensionsLoading } = useQuery<{
    dimensions: { data: { id: number; name: string; icon: string; bigquery_id: string }[] };
  }>(QUERY_DIMENSIONS, {
    variables: {
      pagination: { page: 1, limit: 10 },
      language: currentLanguage,
    },
  });

  const layerOptions = useMemo(() => {
    const dimensions = dimensionsData?.dimensions?.data ?? [];
    return [
      {
        label: t('page.municipality.municipality.card.scores.footer'),
        value: -10,
        icon: <DynamicIcon name="BarChart" color={theme.palette.action.active} />,
      },
      ...dimensions.map((dimension) => ({
        label: dimension.name,
        value: dimension.id,
        icon: <DynamicIcon name={dimension.icon} color={theme.palette.action.active} />,
      })),
    ];
  }, [dimensionsData, t, theme.palette.action.active]);

  const mapperDimensionId = selectedLayer === -10 ? 0 : selectedLayer;

  const geometry = useMemo(() => comparativeRowsToMapGeometry(data, mapperDimensionId), [
    data,
    mapperDimensionId,
  ]);

  const hasDimensionsScores = mapperDimensionId !== 0;

  const dimensionFromQuery = dimensionsData?.dimensions?.data?.find(
    (d) => d.id === mapperDimensionId
  );

  const effectiveBigqueryId = hasDimensionsScores
    ? dimensionFromQuery?.bigquery_id ?? selectedBigqueryId
    : undefined;
  const effectiveDimensionName = hasDimensionsScores
    ? dimensionFromQuery?.name ?? selectedDimensionName
    : undefined;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: MAP_HEIGHT,
        minHeight: MAP_HEIGHT,
        borderRadius: 1,
        overflow: 'visible',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: theme.spacing(3),
          right: theme.spacing(3),
          zIndex: 12,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        <LegendButton variant={mapVariant} noBorderBottom embedMode />
        <ComparativeMapLayerButton noBorderTop onOpen={() => setLayersOpen(true)} />
      </Box>

      <ComparativeMapLayersOverlay
        layers={layerOptions}
        loading={dimensionsLoading}
        selectedLayer={selectedLayer}
        onLayerChange={setSelectedLayer}
        open={layersOpen}
        onClose={() => setLayersOpen(false)}
      />

      <Box
        sx={{
          position: 'absolute',
          bottom: theme.spacing(1.5),
          left: theme.spacing(2),
          right: theme.spacing(2),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ pointerEvents: 'auto' }}>
          <FloatingButtons
            variant={mapVariant}
            onClick={setMapVariant}
            disabled={{
              risk: effectiveBigqueryId === 'economica',
              opportunity: effectiveBigqueryId === 'seguranca',
            }}
          />
        </Box>
      </Box>

      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <Map
          geometry={geometry}
          variant={mapVariant}
          hasDimensionsScores={hasDimensionsScores}
          selectedBigqueryId={hasDimensionsScores ? effectiveBigqueryId : undefined}
          selectedDimensionId={hasDimensionsScores ? mapperDimensionId : undefined}
          selectedDimensionName={hasDimensionsScores ? effectiveDimensionName : undefined}
          mapStyleOverride={MAPBOX_STYLE_LIGHT_V11}
        />
      </Suspense>
    </Box>
  );
};
